import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  Search,
  Grid3X3,
  List,
  Filter,
  Users,
  Briefcase,
  BarChart3,
  Sprout,
  Target,
  Settings,
  LogOut,
  Calendar,
  X,
  Layers,
  Gift,
  Mail,
  Film,
  BookOpen,
  ClipboardList,
  ClipboardCheck,
  Command,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { ToolbarButton } from "../ui/ToolbarButton";
import { FormSelect } from "../ui/FormControls";

export const ZoneTwoToolbar = () => {
  const {
    palette,
    members,
    filteredMembers,
    search,
    setSearch,
    filterPathway,
    setFilterPathway,
    filterFlag,
    setFilterFlag,
    filterDirector,
    setFilterDirector,
    sortBy,
    setSortBy,
    viewMode,
    setViewMode,
    navigate,
    directors,
    handleLogout,
    pathways,
    can,
    showLapsed,
    setShowLapsed,
    lapsedCount,
    cultivationQueue,
    showCultivationQueue,
    setShowCultivationQueue,
    currentView,
    taskBadgeCount,
    setShowTaskDrawer,
    setShowQuickBar,
  } = useApp();

  const [showFilters, setShowFilters] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  const unassignedCount = members.filter(
    (m) => !m.assignedDirector?.trim()
  ).length;

  // Global search dropdown — top 8 matches
  const searchResults = useMemo(() => {
    if (!search.trim() || search.trim().length < 2) return [];
    const query = search.toLowerCase().trim();
    return members
      .filter((member) => {
        const fullName = `${member.firstName || ""} ${member.lastName || ""}`.toLowerCase();
        const reverseName = `${member.lastName || ""}, ${member.firstName || ""}`.toLowerCase();
        const org = (member.organization || "").toLowerCase();
        const email = (member.email || "").toLowerCase();
        const donorId = (member.donorId || member.afpId || "").toLowerCase();
        return (
          fullName.includes(query) ||
          reverseName.includes(query) ||
          org.includes(query) ||
          email.includes(query) ||
          donorId.includes(query)
        );
      })
      .slice(0, 8);
  }, [search, members]);

  const handleResultClick = (member) => {
    setSearch("");
    setSearchFocused(false);
    navigate("memberDetail", member);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (event) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(event.target))
      ) {
        setSearchFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Count prospects for the filter label
  const prospectCount = members.filter((m) => m.pathway === "prospect").length;

  return (
    <div
      style={{
        background: palette.toolbarBg,
        borderBottom: `2px solid ${palette.toolbarBorder || palette.cardBorder}`,
        boxShadow: palette.toolbarShadow || "none",
        fontFamily: FONT_FAMILY,
        position: "relative",
        zIndex: 10,
      }}
    >
      {/* Row 1: Search + Actions */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "10px 24px",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        {/* Search with clear X */}
        <div style={{ position: "relative", flex: "1 1 280px", maxWidth: 400 }} ref={searchRef}>
          <Search
            size={ICON_SIZES.sm}
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: palette.text3,
              pointerEvents: "none",
            }}
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            placeholder="Search donors by name, org, email, Donor ID..."
            style={{
              width: "100%",
              padding: "8px 34px 8px 34px",
              borderRadius: 8,
              fontSize: FONT_SIZES.sm,
              fontFamily: FONT_FAMILY,
              outline: "none",
              background: palette.inputBg,
              color: palette.inputColor,
              border: `1px solid ${palette.inputBorder}`,
              transition: "all 0.2s",
            }}
          />
          {search && (
            <button
              onClick={() => { setSearch(""); setSearchFocused(false); }}
              title="Clear search"
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 20,
                height: 20,
                borderRadius: "50%",
                background: "#6b728020",
                border: "none",
                cursor: "pointer",
                padding: 0,
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#6b728040")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "#6b728020")
              }
            >
              <X size={ICON_SIZES.xs} style={{ color: "#6b7280" }} />
            </button>
          )}

          {/* Search results dropdown */}
          {searchFocused && searchResults.length > 0 && (
            <div
              ref={dropdownRef}
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                marginTop: 4,
                background: palette.cardBg,
                border: `1px solid ${palette.cardBorder}`,
                borderRadius: 8,
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                zIndex: 100,
                maxHeight: 320,
                overflowY: "auto",
              }}
            >
              {searchResults.map((member) => {
                const isOrg = member.donorType === "organization";
                const name = isOrg
                  ? member.organization || "Unknown Organization"
                  : member.lastName
                    ? `${member.lastName}, ${member.firstName || ""}`
                    : member.firstName || "Unknown";
                const subtitle = isOrg
                  ? [member.firstName, member.lastName].filter(Boolean).join(" ") || null
                  : member.organization || null;
                return (
                  <div
                    key={member.id}
                    onClick={() => handleResultClick(member)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 12px",
                      cursor: "pointer",
                      borderBottom: `1px solid ${palette.cardBorder}`,
                      transition: "background 0.1s",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = `${palette.accent1}10`}
                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: FONT_SIZES.xs,
                        fontWeight: 600,
                        color: palette.text1,
                        fontFamily: FONT_FAMILY,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}>
                        {name}
                      </div>
                      {subtitle && (
                        <div style={{
                          fontSize: "0.6rem",
                          color: palette.text3,
                          fontFamily: FONT_FAMILY,
                        }}>
                          {subtitle}
                        </div>
                      )}
                    </div>
                    {member.donorId && (
                      <span style={{
                        fontSize: "0.55rem",
                        color: palette.text3,
                        fontFamily: FONT_FAMILY,
                        flexShrink: 0,
                      }}>
                        {member.donorId}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* No results message */}
          {searchFocused && search.trim().length >= 2 && searchResults.length === 0 && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                marginTop: 4,
                background: palette.cardBg,
                border: `1px solid ${palette.cardBorder}`,
                borderRadius: 8,
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                zIndex: 100,
                padding: "12px 16px",
                textAlign: "center",
              }}
            >
              <span style={{ fontSize: FONT_SIZES.xs, color: palette.text3, fontFamily: FONT_FAMILY }}>
                No donors found
              </span>
            </div>
          )}
        </div>

        {/* Count — only on Donors view */}
        {currentView === "donors" && (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span
              style={{
                fontSize: FONT_SIZES.sm,
                color: palette.text2,
                fontWeight: 500,
              }}
            >
              {filteredMembers.length} of {members.length}
            </span>
            {lapsedCount > 0 && (
              <button
                onClick={() => setShowLapsed(!showLapsed)}
                title={
                  showLapsed
                    ? "Show active donors only"
                    : "Include lapsed donors"
                }
                style={{
                  background: showLapsed ? "#fef3c7" : palette.inputBg,
                  border: `1px solid ${
                    showLapsed ? "#f59e0b" : palette.inputBorder
                  }`,
                  borderRadius: 6,
                  padding: "4px 10px",
                  fontSize: FONT_SIZES.xs,
                  fontWeight: 600,
                  fontFamily: FONT_FAMILY,
                  cursor: "pointer",
                  color: showLapsed ? "#92400e" : palette.text3,
                  transition: "all 0.2s",
                  whiteSpace: "nowrap",
                }}
              >
                {showLapsed ? `All (${lapsedCount} lapsed)` : "Active Only"}
              </button>
            )}
          </div>
        )}

        {/* Action Buttons — auto margins balance empty space on each side
            so the icon row sits visually centered in the toolbar */}
        <div
          style={{
            display: "flex",
            gap: 6,
            alignItems: "center",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          {/* Quick command bar — Ctrl+K */}
          <button
            type="button"
            onClick={() => setShowQuickBar(true)}
            title="Quick command bar — Ctrl+K (or Cmd+K). Navigate, search, create tasks. Type /help for the list."
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
              padding: "8px 14px",
              borderRadius: 10,
              border: "none",
              background: `${palette.accent1}10`,
              cursor: "pointer",
              fontFamily: FONT_FAMILY,
              fontSize: "0.72rem",
              fontWeight: 600,
              color: palette.accent1,
              position: "relative",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = `${palette.accent1}20`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = `${palette.accent1}10`;
            }}
          >
            <Command size={ICON_SIZES.xl} />
            <span style={{ display: "flex", alignItems: "center", gap: 2 }}>
              Quick
              <kbd
                style={{
                  fontSize: "0.5rem",
                  fontWeight: 700,
                  padding: "0px 4px",
                  borderRadius: 3,
                  border: `1px solid ${palette.accent1}60`,
                  background: `${palette.accent1}15`,
                  color: palette.accent1,
                  marginLeft: 2,
                  fontFamily: FONT_FAMILY,
                  letterSpacing: "0.02em",
                }}
              >
                ⌘K
              </kbd>
            </span>
          </button>
          {/* Tools group: Filters + Queue — filters are donor-list-specific,
              so we only surface the toggle on that view. */}
          {currentView === "donors" && (
            <ToolbarButton
              icon={Filter}
              label={showFilters ? "Hide" : "Filters"}
              onClick={() => setShowFilters(!showFilters)}
              active={
                showFilters ||
                filterPathway !== "all" ||
                filterFlag !== "all" ||
                filterDirector !== "all"
              }
            />
          )}
          {can("tasks") && (
            <div style={{ position: "relative" }}>
              <ToolbarButton
                icon={ClipboardCheck}
                label="Tasks"
                onClick={() => setShowTaskDrawer(true)}
                active={currentView === "tasks"}
              />
              {taskBadgeCount > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: 0,
                    right: 2,
                    background: "#ef4444",
                    color: "#fff",
                    fontSize: 9,
                    fontWeight: 700,
                    padding: "0px 4px",
                    borderRadius: 6,
                    minWidth: 14,
                    textAlign: "center",
                    lineHeight: "14px",
                    pointerEvents: "none",
                  }}
                >
                  {taskBadgeCount}
                </span>
              )}
            </div>
          )}
          {(cultivationQueue || []).length > 0 && (
            <button
              onClick={() => setShowCultivationQueue(true)}
              title="Cultivation Queue"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
                padding: "8px 14px",
                borderRadius: 10,
                border: "none",
                background: "#10b98115",
                cursor: "pointer",
                fontFamily: FONT_FAMILY,
                fontSize: "0.72rem",
                fontWeight: 600,
                color: "#10b981",
                position: "relative",
              }}
            >
              <ClipboardList size={ICON_SIZES.xl} />
              Queue
              <span
                style={{
                  position: "absolute",
                  top: 0,
                  right: 2,
                  background: "#10b981",
                  color: "#fff",
                  fontSize: 9,
                  fontWeight: 700,
                  padding: "0px 4px",
                  borderRadius: 6,
                  minWidth: 14,
                  textAlign: "center",
                  lineHeight: "14px",
                }}
              >
                {cultivationQueue.length}
              </span>
            </button>
          )}
          {/* Separator: tools vs navigation */}
          <div
            style={{
              width: 1,
              height: 24,
              background: palette.inputBorder,
              margin: "0 2px",
            }}
          />
          {/* Navigation group */}
          <ToolbarButton
            icon={Users}
            label="Donors"
            onClick={() => navigate("donors")}
            active={currentView === "donors"}
          />
          {can("cultivation") && (
            <ToolbarButton
              icon={Sprout}
              label="Cultivate"
              onClick={() => navigate("cultivation")}
            />
          )}
          {can("cultivation") && (
            <ToolbarButton
              icon={Target}
              label="Capacity"
              onClick={() => navigate("capacity")}
            />
          )}
          {can("gifts") && (
            <ToolbarButton
              icon={Gift}
              label="Gifts"
              onClick={() => navigate("gifts")}
            />
          )}
          {can("gifts") && (
            <ToolbarButton
              icon={Mail}
              label="Campaigns"
              onClick={() => navigate("campaigns")}
            />
          )}
          <ToolbarButton
            icon={Film}
            label="Shows"
            onClick={() => navigate("shows")}
          />
          {can("events") && (
            <ToolbarButton
              icon={Calendar}
              label="Events"
              onClick={() => navigate("events")}
            />
          )}
          {can("portfolios") && (
            <ToolbarButton
              icon={Briefcase}
              label="Portfolios"
              onClick={() => navigate("portfolios")}
            />
          )}
          {can("reports") && (
            <ToolbarButton
              icon={BarChart3}
              label="Reports"
              onClick={() => navigate("reports")}
            />
          )}
          <ToolbarButton
            icon={BookOpen}
            label="Playbook"
            onClick={() => navigate("playbook")}
          />
          {can("settings") && (
            <ToolbarButton
              icon={Settings}
              label="Settings"
              onClick={() => navigate("settings")}
            />
          )}
          <div
            style={{
              width: 1,
              height: 24,
              background: palette.inputBorder,
              margin: "0 4px",
            }}
          />
          <ToolbarButton icon={LogOut} label="Out" onClick={handleLogout} />
        </div>
      </div>

      {/* Row 2: Filter bar (expandable) — donor-list only */}
      {showFilters && currentView === "donors" && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 24px 12px",
            flexWrap: "wrap",
            borderTop: `1px solid ${palette.cardBorder}`,
          }}
        >
          <FormSelect
            value={filterPathway}
            onChange={setFilterPathway}
            options={[
              { value: "all", label: "All Pathways" },
              ...pathways.map((p) => ({ value: p.id, label: p.name })),
            ]}
          />
          <FormSelect
            value={filterFlag}
            onChange={setFilterFlag}
            options={[
              { value: "all", label: "All Donors" },
              { value: "members_only", label: "Donors Only (No Prospects)" },
              {
                value: "prospect",
                label: `Prospects${
                  prospectCount > 0 ? ` (${prospectCount})` : ""
                }`,
              },
              { value: "donor", label: "Donors" },
              { value: "board", label: "Board Donors" },
            ]}
          />
          <FormSelect
            value={filterDirector}
            onChange={setFilterDirector}
            options={[
              { value: "all", label: "All Directors" },
              {
                value: "unassigned",
                label: `⚠ Unassigned (${unassignedCount})`,
              },
              ...(directors || []).map((d) => ({
                value: d.name,
                label: d.name,
              })),
            ]}
          />
          <FormSelect
            value={sortBy}
            onChange={setSortBy}
            options={[
              { value: "lastName", label: "Sort: Last Name" },
              { value: "firstName", label: "Sort: First Name" },
              { value: "organization", label: "Sort: Organization" },
              { value: "pathway", label: "Sort: Pathway" },
            ]}
          />
          {(filterPathway !== "all" ||
            filterFlag !== "all" ||
            filterDirector !== "all") && (
            <button
              onClick={() => {
                setFilterPathway("all");
                setFilterFlag("all");
                setFilterDirector("all");
              }}
              style={{
                background: "none",
                border: "none",
                color: palette.accent1,
                cursor: "pointer",
                fontSize: FONT_SIZES.xs,
                fontWeight: 600,
                fontFamily: FONT_FAMILY,
              }}
            >
              ✕ Clear Filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};
